import { getApiBaseUrl } from '@/envProvider';
import { useAuthStore } from '@/stores/authStore';
import router from '@/plugins/router';
import { AuthRoutes } from '@/types/enums/RouteNameEnums';
import type {
    StreamingBufferType,
    StreamingToolCallType,
    StreamingMessageMetadata,
    SimulationProposalType,
} from '@/types/StreamingTypes';
import {
    parseNdjsonLine, parseCompleteChunk, parseCompleteTitle, parseErrorChunk,
} from '@/utils/streamingParser';
import { ChatStreamError } from '@/types/errors/ChatStreamError';

export type StreamingCallbacks = {
    onTextUpdate: (text: string) => void;
    onReasoningUpdate: (reasoning: string) => void;
    onToolCall: (toolCall: StreamingToolCallType) => void;
    onMetadata: (metadata: StreamingMessageMetadata) => void;
    onComplete: (buffer: StreamingBufferType) => void;
    onError: (error: Error) => void;
};

/**
 * Composable for handling streaming chat messages via NDJSON
 */
export const useStreamingChat = () => {
    /**
     * Streams a chat message with real-time updates
     */
    const streamMessage = async (
        userMessage: string,
        messageId: string,
        chatId: number,
        callbacks: StreamingCallbacks,
    ): Promise<void> => {
        return await streamMessageWithRetry(userMessage, messageId, chatId, callbacks, false);
    };

    /**
     * Internal function to handle message sending with token refresh retry logic
     */
    const streamMessageWithRetry = async (
        userMessage: string,
        _messageId: string,
        chatId: number,
        callbacks: StreamingCallbacks,
        isRetry: boolean,
    ): Promise<void> => {
        const authStore = useAuthStore();
        const baseUrl = getApiBaseUrl();
        const url = `${baseUrl}/ai-chats/${chatId}/stream`;

        // Setup request headers
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (authStore.authToken) {
            headers.Authorization = `Bearer ${authStore.authToken.access_token}`;
        }

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers,
                body: JSON.stringify({ message: userMessage }),
            });

            // Handle 401 unauthorized - attempt token refresh
            if (response.status === 401 && !isRetry && authStore.authToken?.refresh_token) {
                try {
                    await authStore.refreshToken();

                    // Retry the request with refreshed token
                    return await streamMessageWithRetry(userMessage, _messageId, chatId, callbacks, true);
                } catch {
                    authStore.clearSession();
                    router.push({ name: AuthRoutes.Login });
                    throw new Error('Authentication failed and token refresh unsuccessful');
                }
            }

            if (!response.ok) {
                if (response.status === 401) {
                    // Already retried or no refresh token available
                    authStore.clearSession();
                    router.push({ name: AuthRoutes.Login });
                    throw new Error('Authentication failed');
                }
                throw new Error(`Request failed: ${response.statusText}`);
            }

            // Check if we have a stream body
            if (!response.body) {
                throw new Error('No response body for streaming');
            }

            // Process the NDJSON stream
            const buffer: StreamingBufferType = {
                textBuffer: '',
                reasoningBuffer: '',
                toolCalls: [],
                metadata: {},
                isComplete: false,
                simulationProposals: [],
            };

            await processNdjsonStream(response.body, buffer, callbacks);
        } catch (error) {
            const requestError = error instanceof Error ? error : new Error('Unknown error');
            callbacks.onError(requestError);
            throw requestError;
        }
    };

    /**
     * Processes the NDJSON stream from the response body
     */
    const processNdjsonStream = async (
        body: ReadableStream<Uint8Array>,
        buffer: StreamingBufferType,
        callbacks: StreamingCallbacks,
    ): Promise<void> => {
        const reader = body.getReader();
        const decoder = new TextDecoder();
        let partialLine = '';

        try {
            while (true) {
                const { done, value } = await reader.read();

                if (done) {
                    break;
                }

                const chunk = decoder.decode(value, { stream: true });
                const text = partialLine + chunk;
                const lines = text.split('\n');

                // Keep the last potentially incomplete line
                partialLine = lines.pop() ?? '';

                // Process complete lines
                for (const line of lines) {
                    processNdjsonLine(line, buffer, callbacks);
                }
            }

            // Process any remaining partial line
            if (partialLine.trim()) {
                processNdjsonLine(partialLine, buffer, callbacks);
            }

            // Mark as complete
            if (!buffer.isComplete) {
                buffer.isComplete = true;
                callbacks.onComplete(buffer);
            }
        } finally {
            reader.releaseLock();
        }
    };

    /**
     * Processes a single NDJSON line and updates buffer/calls callbacks
     */
    const processNdjsonLine = (line: string, buffer: StreamingBufferType, callbacks: StreamingCallbacks): void => {
        const chunk = parseNdjsonLine(line);

        if (chunk) {
            // Update buffers from the server-accumulated fields
            buffer.textBuffer = chunk.text;
            buffer.reasoningBuffer = chunk.reasoning;

            if (chunk.type === 'message') {
                callbacks.onTextUpdate(chunk.text);
            }

            if (chunk.type === 'reasoning') {
                callbacks.onReasoningUpdate(chunk.reasoning);
            }

            return;
        }

        // Server-rendered error event (e.g. upstream 429 daily limit)
        const errorChunk = parseErrorChunk(line);
        if (errorChunk) {
            buffer.isComplete = true;
            callbacks.onError(new ChatStreamError(errorChunk.content, errorChunk.statusCode));

            return;
        }

        // Check for simulation_proposals in complete event
        const proposals = parseCompleteChunk(line);
        if (proposals.length > 0) {
            buffer.simulationProposals = proposals;
        }

        // Server may refresh the conversation title inside the complete event.
        const title = parseCompleteTitle(line);
        if (title) {
            buffer.conversationTitle = title;
        }
    };

    /**
     * Streams a decision response for a selected proposal
     */
    const streamDecision = async (
        agentRecommendation: string,
        modifiedParams: SimulationProposalType,
        chatId: number,
        callbacks: StreamingCallbacks,
    ): Promise<void> => {
        return await streamDecisionWithRetry(agentRecommendation, modifiedParams, chatId, callbacks, false);
    };

    const streamDecisionWithRetry = async (
        agentRecommendation: string,
        modifiedParams: SimulationProposalType,
        chatId: number,
        callbacks: StreamingCallbacks,
        isRetry: boolean,
    ): Promise<void> => {
        const authStore = useAuthStore();
        const baseUrl = getApiBaseUrl();
        const url = `${baseUrl}/ai-chats/${chatId}/decisions/stream`;

        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (authStore.authToken) {
            headers.Authorization = `Bearer ${authStore.authToken.access_token}`;
        }

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    agent_recommendation: agentRecommendation,
                    modified_params: modifiedParams,
                }),
            });

            if (response.status === 401 && !isRetry && authStore.authToken?.refresh_token) {
                try {
                    await authStore.refreshToken();

                    return await streamDecisionWithRetry(agentRecommendation, modifiedParams, chatId, callbacks, true);
                } catch {
                    authStore.clearSession();
                    router.push({ name: AuthRoutes.Login });
                    throw new Error('Authentication failed and token refresh unsuccessful');
                }
            }

            if (!response.ok) {
                if (response.status === 401) {
                    authStore.clearSession();
                    router.push({ name: AuthRoutes.Login });
                    throw new Error('Authentication failed');
                }
                throw new Error(`Request failed: ${response.statusText}`);
            }

            if (!response.body) {
                throw new Error('No response body for streaming');
            }

            const buffer: StreamingBufferType = {
                textBuffer: '',
                reasoningBuffer: '',
                toolCalls: [],
                metadata: {},
                isComplete: false,
                simulationProposals: [],
            };

            await processNdjsonStream(response.body, buffer, callbacks);
        } catch (error) {
            const requestError = error instanceof Error ? error : new Error('Unknown error');
            callbacks.onError(requestError);
            throw requestError;
        }
    };

    return { streamMessage, streamDecision };
};
